import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, Trophy, Globe, Star, LayoutGrid, X } from "lucide-react";
import { useFbFavs } from "@/lib/useFbFavs";
import { FavStar, TeamLogo } from "@/components/football/MatchRow";

const Section = ({ title, id, children, defaultOpen = true, count }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-[#273042] first:border-t-0">
      <button onClick={() => setOpen(!open)} data-testid={`fb-section-${id}`} aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-3 text-[11px] uppercase tracking-wider font-bold text-slate-400 hover:text-white">
        <span>{title}{count != null && <span className="ml-1.5 text-slate-600">{count}</span>}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="px-2 pb-3 space-y-0.5">{children}</div>}
    </div>
  );
};

const CompItem = ({ c, active, onPick }) => (
  <div className={`flex items-center rounded-lg transition-colors ${active ? "bg-[#E11D2A]/15" : "hover:bg-white/5"}`}>
    <button onClick={() => onPick(c)} data-testid={`fb-comp-${c.slug}`} className="flex-1 min-w-0 flex items-center gap-2.5 px-2 py-2 text-left">
      <span className="w-7 h-7 rounded-md bg-[#0A0C10] border border-[#273042] flex items-center justify-center shrink-0 overflow-hidden">
        {c.logoUrl ? <img src={c.logoUrl} alt="" loading="lazy" className="w-5 h-5 object-contain" /> : <Trophy className="w-4 h-4 text-[#E11D2A]" />}
      </span>
      <span className="min-w-0"><span className={`block text-sm truncate ${active ? "text-white font-semibold" : "text-slate-200"}`}>{c.name}</span>
        <span className="block text-[11px] text-slate-500 truncate">{c.country}</span></span>
    </button>
    <FavStar kind="competitions" testid={`fav-comp-${c.slug}`} item={{ id: c.slug, name: c.name, slug: c.slug, country: c.country, logoUrl: c.logoUrl }} />
  </div>
);

// Countries + competitions + favourites — compact, collapsible, searchable.
export function FootballSidebar({ comps, selected, onPick, search, setSearch, onClose }) {
  const { favs } = useFbFavs();
  const q = search.trim().toLowerCase();
  const match = (c) => !q || `${c.name} ${c.country}`.toLowerCase().includes(q);
  const byCountry = comps.filter(match).reduce((acc, c) => { (acc[c.country || "Other"] = acc[c.country || "Other"] || []).push(c); return acc; }, {});
  const countries = Object.keys(byCountry).sort((a, b) => (a === "Rwanda" ? -1 : b === "Rwanda" ? 1 : a.localeCompare(b)));
  const favComps = comps.filter((c) => favs.competitions.some((f) => f.id === c.slug));
  const favTeams = favs.teams.filter((t) => !q || (t.name || "").toLowerCase().includes(q));

  return (
    <div className="rounded-2xl bg-[#0F131B] border border-[#273042] overflow-hidden" data-testid="football-sidebar">
      <div className="flex items-center gap-2 px-3 py-3 border-b border-[#273042]">
        <Search className="w-4 h-4 text-slate-500 shrink-0" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search teams, competitions…" data-testid="fb-search-input"
          className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder-slate-500 outline-none h-8" />
        {search && <button onClick={() => setSearch("")} data-testid="fb-search-clear" className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>}
        {onClose && <button onClick={onClose} data-testid="fb-sidebar-close" className="text-slate-400 hover:text-white ml-1"><X className="w-5 h-5" /></button>}
      </div>
      <div className="max-h-[calc(100vh-11rem)] overflow-y-auto overscroll-contain">
        <div className="px-2 pt-2">
          <button onClick={() => onPick(null)} data-testid="fb-comp-all"
            className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm ${!selected ? "bg-[#E11D2A]/15 text-white font-semibold" : "text-slate-200 hover:bg-white/5"}`}>
            <LayoutGrid className="w-4 h-4 text-[#E11D2A]" /> All competitions
          </button>
        </div>
        {(favTeams.length > 0 || favComps.length > 0) && (
          <Section title="Favourites" id="favourites" count={favTeams.length + favComps.length}>
            {favTeams.map((t) => (
              <Link key={t.id} to={`/football/team/${t.id}`} onClick={onClose} data-testid={`fb-fav-team-${t.id}`} className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-white/5">
                <TeamLogo team={t} size="w-6 h-6" /><span className="text-sm text-slate-200 truncate">{t.name}</span><Star className="w-3.5 h-3.5 ml-auto fill-amber-400 text-amber-400" />
              </Link>
            ))}
            {favComps.map((c) => <CompItem key={`f-${c.id}`} c={c} active={selected?.slug === c.slug} onPick={onPick} />)}
          </Section>
        )}
        {countries.map((country) => (
          <Section key={country} id={`country-${country.toLowerCase().replace(/\s+/g, "-")}`} count={byCountry[country].length}
            title={<span className="inline-flex items-center gap-1.5">{country === "Rwanda" ? <Trophy className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}{country}</span>}
            defaultOpen={!!q || countries.length <= 6 || byCountry[country].some((c) => c.slug === selected?.slug)}>
            {byCountry[country].map((c) => <CompItem key={c.id} c={c} active={selected?.slug === c.slug} onPick={onPick} />)}
          </Section>
        ))}
        {countries.length === 0 && <p className="text-sm text-slate-500 text-center py-6" data-testid="fb-sidebar-empty">No competitions match “{search}”.</p>}
      </div>
    </div>
  );
}
